import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  MapPin,
  Beef,
  CreditCard,
  Loader2,
  UserX,
  Clock,
  Copy,
  Check,
  KeyRound,
  ClipboardList,
  Phone,
  FileText,
  Eye,
  EyeOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useProducer, useProducerOtp } from '@/hooks/useSupport';
import type { Conversation, Producer, ProducerActivity, SupportOtp } from '@/api/endpoints/support';

function initials(name?: string) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Ativo',
  trial: 'Em teste',
  past_due: 'Pendente',
  canceled: 'Cancelado',
  expired: 'Expirado',
};

function PlanBadge({ producer }: { producer: Producer }) {
  const sub = producer.assinatura;
  const herdada = producer.coberturaHerdada;
  if (sub?.status) {
    const status = sub.status;
    const tone =
      status === 'active'
        ? 'bg-tertiary/15 text-tertiary'
        : status === 'trial'
          ? 'bg-primary/15 text-primary'
          : 'bg-secondary/15 text-secondary';
    return (
      <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', tone)}>
        {STATUS_LABEL[status] || status}
      </span>
    );
  }
  if (herdada) {
    return (
      <span className="rounded-full bg-tertiary/15 px-2 py-0.5 text-[11px] font-semibold text-tertiary">
        Coberto
      </span>
    );
  }
  return (
    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-text-tertiary">
      Sem plano
    </span>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2.5 px-4 py-2.5">
      <span className="mt-0.5 text-text-tertiary">{icon}</span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</p>
        <p className="truncate text-sm text-text-title">{value}</p>
      </div>
    </div>
  );
}

function ActivityItem({ item }: { item: ProducerActivity }) {
  return (
    <div className="flex gap-2.5">
      <span className="mt-1 flex size-1.5 shrink-0 rounded-full bg-primary/50" />
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-text-title">{item.title}</p>
        {item.subtitle && (
          <p className="truncate text-[11px] text-text-secondary">{item.subtitle}</p>
        )}
      </div>
    </div>
  );
}

const OTP_STATUS_LABEL: Record<SupportOtp['status'], string> = {
  active: 'Válido',
  expired: 'Expirado',
  used: 'Utilizado',
};

function formatOtpTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

// Quanto tempo o código fica na tela depois de revelado. Curto de propósito:
// ele dá acesso à conta do produtor e não pode ficar exposto num monitor aberto
// no meio do escritório.
const OTP_VISIVEL_MS = 20000;

function OtpCard({ phone }: { phone?: string }) {
  const [copied, setCopied] = useState(false);
  const [revelado, setRevelado] = useState(false);

  // A busca só acontece enquanto o código está à mostra: nada de código
  // trafegando de 15 em 15 segundos sem ninguém ter pedido.
  const { data, isFetching } = useProducerOtp(phone, revelado);
  const otp = data?.otp;

  // Trocou de cliente: esconde de novo.
  useEffect(() => {
    setRevelado(false);
  }, [phone]);

  useEffect(() => {
    if (!revelado) return;
    const t = window.setTimeout(() => setRevelado(false), OTP_VISIVEL_MS);
    return () => window.clearTimeout(t);
  }, [revelado]);

  if (!phone) return null;

  // Ainda não pediram: o card existe, mas não sabemos (nem buscamos) nada.
  if (!revelado && !otp) {
    return (
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
            <KeyRound size={12} /> Último código OTP
          </p>
          <button
            type="button"
            onClick={() => setRevelado(true)}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-[11px] font-semibold text-text-secondary transition-colors hover:bg-muted hover:text-text-title disabled:opacity-50"
          >
            {isFetching ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
            Ver código
          </button>
        </div>
      </div>
    );
  }

  if (!otp) {
    return (
      <div className="border-b border-border px-4 py-3">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
          <KeyRound size={12} /> Último código OTP
        </p>
        <p className="mt-1 text-xs text-text-tertiary">
          {isFetching ? 'Buscando…' : 'Nenhum código gerado para este contato.'}
        </p>
      </div>
    );
  }

  // Copiar não exige revelar: dá pra colar no cliente sem o código passar pela
  // tela — e é o caminho que menos expõe.
  const copy = async () => {
    await navigator.clipboard.writeText(otp.code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const active = otp.status === 'active';
  const mascarado = '•'.repeat(otp.code.length);
  return (
    <div className="border-b border-border px-4 py-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
          <KeyRound size={12} /> Último código OTP
        </p>
        <span
          className={cn(
            'text-[11px] font-semibold',
            active ? 'text-tertiary' : 'text-text-tertiary'
          )}
        >
          {OTP_STATUS_LABEL[otp.status]}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            'font-mono text-xl font-bold tracking-[0.18em]',
            active ? 'text-text-title' : 'text-text-tertiary'
          )}
        >
          {revelado ? otp.code : mascarado}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => setRevelado((v) => !v)}
            className="inline-flex size-8 items-center justify-center rounded border border-border text-text-secondary transition-colors hover:bg-muted hover:text-text-title"
            aria-label={revelado ? 'Ocultar código' : 'Revelar código'}
            title={revelado ? 'Ocultar' : 'Revelar por 20 segundos'}
          >
            {revelado ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
          <button
            type="button"
            onClick={copy}
            className="inline-flex size-8 items-center justify-center rounded border border-border text-text-secondary transition-colors hover:bg-muted hover:text-text-title"
            aria-label="Copiar código OTP"
            title="Copiar sem mostrar na tela"
          >
            {copied ? <Check size={15} className="text-tertiary" /> : <Copy size={15} />}
          </button>
        </span>
      </div>
      <p className="mt-1 text-[11px] text-text-tertiary">
        Gerado às {formatOtpTime(otp.createdAt)} · expira às {formatOtpTime(otp.expiresAt)}
      </p>
    </div>
  );
}

// Rótulos das categorias que o bot de triagem grava em `bot_category` (slug).
const TRIAGE_CATEGORY_LABEL: Record<string, string> = {
  tecnico: 'Suporte técnico',
  financeiro: 'Financeiro',
  'novo-usuario': 'Novo usuário',
  'compra-brinco': 'Compra de brinco',
  'compra-leitor': 'Compra de leitor',
};

// Resumo do que o bot apurou antes de entregar pro humano. Fica fixo no topo
// porque a nota interna equivalente nasce no começo da conversa — e o atendente
// não deveria ter que rolar o histórico inteiro pra saber com quem está falando.
function TriageCard({ conversation }: { conversation?: Conversation | null }) {
  const attrs = conversation?.custom_attributes;
  if (!attrs) return null;

  const category = typeof attrs.bot_category === 'string' ? attrs.bot_category : null;
  const identified = attrs.bot_identified === true;
  const producerName = typeof attrs.bot_producer_name === 'string' ? attrs.bot_producer_name : null;
  const plano = typeof attrs.bot_plano === 'string' && attrs.bot_plano ? attrs.bot_plano : null;
  // O bot grava o que o cliente descreveu no handoff; o resumo da IA (quando
  // ativa) entra por cima, porque é redigido para quem vai assumir.
  const problema = typeof attrs.bot_problem === 'string' ? attrs.bot_problem : null;
  const resumoIa = typeof attrs.ia_resumo === 'string' ? attrs.ia_resumo : null;
  if (!category && !identified && !producerName && !problema) return null;

  return (
    <div className="border-b border-border bg-primary/5 px-4 py-3">
      <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
        <ClipboardList size={12} /> Triagem
      </p>
      {category && (
        <p className="text-sm font-semibold text-text-title">
          {TRIAGE_CATEGORY_LABEL[category] ?? category}
        </p>
      )}
      <p className="mt-0.5 text-xs text-text-secondary">
        {identified
          ? `Identificado como ${producerName ?? 'cliente'}${plano ? ` · ${plano}` : ''}`
          : 'Não identificado como cliente'}
      </p>
      {(resumoIa || problema) && (
        <p className="mt-2 border-t border-border pt-2 text-xs leading-relaxed text-text-title">
          {resumoIa ?? problema}
        </p>
      )}
    </div>
  );
}

// Estado para quem não está cadastrado: em vez de uma coluna vazia, mostra o que
// se sabe do contato e o caminho natural (orçamento) quando é lead de compra.
function LeadPanel({ phone, conversation }: { phone: string; conversation?: Conversation | null }) {
  const name = conversation?.meta?.sender?.name;
  const category = conversation?.custom_attributes?.bot_category;
  const isPurchaseLead = typeof category === 'string' && category.startsWith('compra-');

  return (
    <>
      <div className="flex flex-col items-center gap-2 border-b border-border p-5 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-secondary/10 text-secondary">
          <UserX size={22} />
        </span>
        <div>
          <p className="text-sm font-bold text-text-title">{name || 'Contato sem cadastro'}</p>
          <p className="text-xs text-text-secondary">{phone}</p>
        </div>
        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-text-tertiary">
          Não cadastrado
        </span>
      </div>

      <TriageCard conversation={conversation} />

      <div className="divide-y divide-border border-b border-border">
        <InfoRow icon={<Phone size={15} />} label="Telefone" value={phone} />
        {conversation?.labels?.length ? (
          <InfoRow
            icon={<ClipboardList size={15} />}
            label="Etiquetas"
            value={conversation.labels.join(', ')}
          />
        ) : null}
      </div>

      <div className="space-y-2 p-4">
        <p className="text-xs text-text-tertiary">
          Este número não bate com nenhum produtor no sistema.
          {isPurchaseLead ? ' O contato veio por interesse de compra.' : ''}
        </p>
        <Link
          to="/orcamentos"
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-semibold text-text-secondary transition-colors hover:bg-muted"
        >
          <FileText size={14} /> Abrir orçamentos
        </Link>
      </div>
    </>
  );
}

// Painel lateral do produtor: casa o telefone do contato com o nosso usuário e
// mostra plano/propriedade/animais/atividade ao lado do chat.
export function ProducerPanel({
  phone,
  conversation,
  className = 'hidden lg:flex',
}: {
  phone?: string;
  conversation?: Conversation | null;
  // Onde e quando aparece: coluna fixa por padrão; a Central passa outro
  // valor para usá-lo também dentro do painel sobreposto (tablet e celular).
  className?: string;
}) {
  const { data, isLoading } = useProducer(phone);
  const producer = data?.producer;
  const activity = data?.activity ?? [];

  if (!phone) return null;

  return (
    <aside
      className={cn(
        'w-72 shrink-0 flex-col overflow-y-auto border-l border-border bg-card',
        className
      )}
    >
      {isLoading ? (
        <div className="flex h-full items-center justify-center text-text-tertiary">
          <Loader2 className="animate-spin" size={20} />
        </div>
      ) : !producer ? (
        <LeadPanel phone={phone} conversation={conversation} />
      ) : (
        <>
          <div className="flex flex-col items-center gap-2 border-b border-border p-5 text-center">
            <span className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
              {initials(producer.name)}
            </span>
            <div>
              <p className="text-sm font-bold text-text-title">{producer.name}</p>
              <p className="text-xs text-text-secondary">{producer.phone}</p>
            </div>
            <PlanBadge producer={producer} />
          </div>

          <TriageCard conversation={conversation} />

          <OtpCard phone={phone} />

          <div className="divide-y divide-border border-b border-border">
            <InfoRow
              icon={<CreditCard size={15} />}
              label="Plano"
              value={
                producer.assinatura?.plano?.nome ||
                producer.coberturaHerdada?.plano?.nome ||
                (producer.isProducer ? 'Produtor sem plano' : 'Funcionário')
              }
            />
            <InfoRow
              icon={<MapPin size={15} />}
              label="Propriedade"
              value={
                producer.property?.name
                  ? `${producer.property.name}${producer.property.uf ? ` — ${producer.property.uf}` : ''}`
                  : 'Sem propriedade'
              }
            />
            <InfoRow
              icon={<Beef size={15} />}
              label="Animais"
              value={String(producer.animalCount ?? 0)}
            />
            <InfoRow
              icon={<User size={15} />}
              label="Vínculo"
              value={producer.isProducer ? 'Proprietário' : 'Funcionário'}
            />
          </div>

          <div className="p-4">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
              <Clock size={12} /> Atividade recente
            </p>
            {activity.length === 0 ? (
              <p className="text-xs text-text-tertiary">Sem atividade registrada.</p>
            ) : (
              <div className="space-y-2.5">
                {activity.map((a, i) => (
                  <ActivityItem key={i} item={a} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
